import React from 'react';
import PropTypes from 'prop-types';
import { useLabels } from 'wsm-common-data';
import FacetGroup from '../FacetGroup';
import FacetTitle from '../FacetTitle';
import { FACET_OVERRIDE_TYPE_SELECT } from '../../global-constants';

const NestedFacetPanel = ({
	facetGroup,
	allFiltersLabel,
	onBack,
	backLinkRef
}) => {
	const {
		facet,
		selectedValues = [],
		detailsLinkData,
		openDetailsModal,
		isMultiFacetGroup,
		multiFacets,
		groupId: multiGroupId,
		groupTitleLabel
	} = facetGroup;

	const labels = useLabels();

	// Derive id and title the same way MultiFacetGroup does: multi-group
	// items carry groupId/groupTitleLabel directly; single-facet items
	// use the real facet object.
	const groupId = multiGroupId || facet?.id;
	const groupTitle = groupTitleLabel
		? labels.get(groupTitleLabel)
		: facet?.label;

	const hasContentOrLabel =
		detailsLinkData &&
		(detailsLinkData.mainDetail ||
			detailsLinkData.subContent?.length !== 0);

	const renderFacetContent = () => {
		if (isMultiFacetGroup && multiFacets) {
			return (
				<>
					{multiFacets.map(({ facet: subFacet, facetSelections }) => (
						<FacetGroup
							facet={subFacet}
							selectedValues={
								facetSelections ? facetSelections.values : []
							}
							renderPanel={false}
							includeExtras={false}
							overrideType={FACET_OVERRIDE_TYPE_SELECT}
							key={`nested-multi-${subFacet.id}`}
						/>
					))}
					{hasContentOrLabel && (
						<button
							type="button"
							className="text-link mt-3 text-decoration-underline text-primary text-left"
							onClick={() =>
								openDetailsModal(
									groupId,
									groupTitle,
									detailsLinkData,
									true
								)
							}
						>
							<span className="ddc-font-size-xsmall">
								{labels.get('ABOUT')} {groupTitle}
							</span>
						</button>
					)}
				</>
			);
		}

		return (
			<FacetGroup
				facet={facet}
				selectedValues={selectedValues}
				renderPanel={false}
				detailsLinkData={detailsLinkData}
				openDetailsModal={openDetailsModal}
			/>
		);
	};

	return (
		<div
			className="nested-facet-detail"
			role="region"
			aria-label={groupTitle}
		>
			<button
				type="button"
				ref={backLinkRef}
				className="btn btn-unstyled d-flex align-items-center pt-3 px-0 border-0 border-bottom w-100 text-left"
				style={{ paddingBottom: '40px' }}
				onClick={onBack}
				aria-label={`${labels.get('BACK_TO')} ${allFiltersLabel}`}
			>
				<i
					className="ddc-icon ddc-icon-skinny-arrow-left mr-2 text-link"
					aria-hidden="true"
				/>
				<span className="text-link text-decoration-underline">
					{allFiltersLabel}
				</span>
			</button>
			<div className="nested-facet-detail-title py-3">
				<FacetTitle
					facetTitle={groupTitle}
					groupId={groupId}
					showIcon={false}
					extraClassName="h3"
				/>
			</div>
			<div className="nested-facet-detail-content pt-3">
				{renderFacetContent()}
			</div>
		</div>
	);
};

NestedFacetPanel.propTypes = {
	facetGroup: PropTypes.shape({
		facet: PropTypes.shape({
			id: PropTypes.string.isRequired,
			label: PropTypes.string.isRequired
		}),
		groupId: PropTypes.string,
		groupTitleLabel: PropTypes.string,
		selectedValues: PropTypes.arrayOf(PropTypes.shape({})),
		detailsLinkData: PropTypes.shape({
			mainDetail: PropTypes.string,
			subContent: PropTypes.arrayOf(PropTypes.shape({}))
		}),
		openDetailsModal: PropTypes.func,
		isMultiFacetGroup: PropTypes.bool,
		multiFacets: PropTypes.arrayOf(PropTypes.shape({}))
	}).isRequired,
	allFiltersLabel: PropTypes.string.isRequired,
	onBack: PropTypes.func.isRequired,
	backLinkRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) })
};

export default NestedFacetPanel;
